import type { Ref } from 'react';
import type { RelationCardProps } from '../RelationCard';

import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../../lib/helpers';

import RelationCard from '../../../components/RelationCard/RelationCard';

import { DragIcon } from '../../../images/shapes';

interface DraggableRelationCardProps extends RelationCardProps {
  /**
   * On card drop
   */
  onDrop: (sourceDataUrl: string, targetDataUrl: string) => void;
  /**
   * Relation data URL
   */
  dataUrl: string;
  /**
   * Current relations length
   */
  relationsLength: number;
}

const DraggableRelationCard = ({
  onDrop,
  dataUrl,
  disabled,
  relationsLength,
  additionalContainerClasses,
  testId,
  ...cardProps
}: DraggableRelationCardProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [, drop] = useDrop<{ dataUrl: string }>({
    accept: 'relation-card',
    canDrop: () => !disabled,
    hover: (item, monitor) => {
      if (!ref.current) {
        return;
      }

      if (monitor.canDrop() && !monitor.didDrop() && !monitor.isOver()) {
        onDrop(item.dataUrl, dataUrl);
      }
    },
  });

  const [{ isDragging }, drag, preview] = useDrag({
    item: { dataUrl },
    type: 'relation-card',
    canDrag: () => !disabled,
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  const connectDragDropRef = (node: HTMLDivElement | null) => {
    if (node) {
      ref.current = node;
      drop(node);
      preview(node);
    }
  };

  return (
    <RelationCard
      {...cardProps}
      ref={connectDragDropRef}
      dragElement={
        relationsLength > 1 ? (
          <div
            className={twMerge(
              'flex items-center w-fit px-1.5 rounded-r-lg hover:opacity-70 cursor-grab',
              'bg-slate-100 dark:bg-slate-700 border border-l-0 border-slate-400 dark:border-slate-600',
              isDragging && 'cursor-grabbing',
              disabled && 'cursor-not-allowed',
            )}
            {...getTestProps(testId, 'drag-icon')}
            ref={drag as unknown as Ref<HTMLDivElement>}
          >
            <DragIcon className="text-slate-400 w-2" />
          </div>
        ) : null
      }
      disabled={disabled}
      additionalContainerClasses={twMerge(
        additionalContainerClasses,
        isDragging ? 'opacity-50 shadow-lg ' : '',
      )}
      testId={testId}
    />
  );
};

export default DraggableRelationCard;
